import React, { useMemo } from 'react';

import { AnimatePresence } from 'framer-motion';

import { FiltersSidebar } from '@pkg/components/filters';
import { useAppSelector, useFetchOnScroll, useProfileCollectibles } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { useNFTsFiltersStore, useNFTsPageStore } from '@app/store/zustand';

import { PROFILE_PAGE_WRAPPER_ID } from '../../constants';
import { useIsPreview } from '../../hooks/useIsPreview';

import EmptyText from './components/EmptyText/EmptyText';
import { GridView } from './components/GridView';
import { ListView } from './components/ListView';
import { SelectAllFloater } from './components/SelectAllFloater';
import { NftFiltersPrimaryDesktop, NftFiltersPrimaryMobile, NftFiltersSecondary } from './parts';

import { Columns, Rows, StyledNftSection } from './NFTSection.style';

export default function NFTSection() {
    const { tablet } = useMediaQuery();
    const isPreview = useIsPreview();

    const isPageOwner = useAppSelector((state) => state.yatPage.isPageOwner);

    const {
        items: nftEntries,
        itemCount,
        isLoading,
        isFetching,
        hasNextPage,
        fetchNextPage,
    } = useProfileCollectibles();
    const { showFilters, isListView } = useNFTsPageStore();

    const search = useNFTsFiltersStore((state) => state.search);
    const sortDirection = useNFTsFiltersStore((state) => state.sortDirection);

    useFetchOnScroll({
        hasNextPage,
        isLoading,
        isFetching,
        onCanFetch: fetchNextPage,
        elementId: PROFILE_PAGE_WRAPPER_ID,
        offset: 700,
    });

    const isEmpty = !isLoading && (itemCount === 0 || !itemCount);
    const gridViewLoading = !isEmpty && !isListView && (isLoading || isFetching);
    const canShowFilters = Boolean(isPageOwner && !isPreview);

    const searchResults = useMemo(() => {
        const safeSearch = search.toLowerCase().trim();

        if (!safeSearch.length || !nftEntries?.length) {
            return nftEntries ?? [];
        }

        // TODO: Search filtering should not be done on the frontend...
        // it should instead be a query we can make to the BE. Client
        // filtering won't work with results outside of the current pagination.
        return nftEntries.filter((entry) => {
            const { name, collection } = entry || {};

            const collectionName = collection?.name ?? '';
            const collectionMatch = collectionName.toLowerCase().includes(safeSearch);

            if (collectionMatch) return true;

            const tokenName = name ?? '';
            const tokenMatch = tokenName.toLowerCase().includes(safeSearch);

            return tokenMatch;
        });
    }, [search, nftEntries]);

    // TODO: `sortDirection` should not simply reverse the results...
    // this needs to be send to the BE based on "sortable field".
    const results = sortDirection === 'asc' ? searchResults.reverse() : searchResults;

    const primaryFiltersMobile = canShowFilters && !tablet ? <NftFiltersPrimaryMobile nftEntries={results} /> : null;

    const primaryFiltersDesktop =
        canShowFilters && tablet ? <NftFiltersPrimaryDesktop resultsCount={nftEntries?.length} /> : null;

    // TODO: Should all filters UI be `disabled` if we have no
    // `nftEntries` results directly from the BE?
    const sidebarMarkup =
        tablet && showFilters ? (
            <FiltersSidebar key="NftFilters-Sidebar">
                <NftFiltersSecondary />
            </FiltersSidebar>
        ) : null;

    const emptyState = isEmpty ? <EmptyText /> : null;

    const listMarkup = isListView && isPageOwner && !isPreview && !isEmpty ? <ListView nftEntries={results} /> : null;

    const gridMarkup =
        !isListView && !isEmpty ? (
            <GridView isOwner={isPageOwner && !isPreview} isLoading={gridViewLoading} nftEntries={results} />
        ) : null;

    const selectAllFooterMarkup = tablet && canShowFilters ? <SelectAllFloater nftEntries={results} /> : null;

    return (
        <>
            <StyledNftSection>
                <Rows>
                    {primaryFiltersMobile || primaryFiltersDesktop}

                    <Columns>
                        <AnimatePresence initial={false}>{sidebarMarkup}</AnimatePresence>
                        {emptyState || listMarkup || gridMarkup}
                    </Columns>
                </Rows>
            </StyledNftSection>

            {selectAllFooterMarkup}
        </>
    );
}
